import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "lenis";
import { BottleScene } from "@/poc/BottleScene";
import { Preloader } from "@/poc/Preloader";
import { windowOpacity } from "@/poc/keyframes";
import { webglAvailable, usePrefersReducedMotion, useIsMobile } from "@/poc/hooks";
import { HOME_CHAPTERS, CATEGORIES_UI, CHANNELS, CH7_STATS } from "@/home/content";
import { WorldRoutes } from "@/home/WorldRoutes";
import { IndiaDotMap } from "@/home/IndiaDotMap";
import { Ch9Section, EndFrameBand } from "@/home/SharedSections";
import StaticHome from "@/home/StaticHome";

gsap.registerPlugin(ScrollTrigger);

const POSITIONS = {
  1: { section: "items-end justify-start", inner: "pb-[13vh] pl-6 sm:pl-[7vw] pr-6 max-w-2xl" },
  2: { section: "items-end md:items-center justify-start md:justify-end", inner: "pb-[10vh] md:pb-0 px-6 md:pl-0 md:pr-[8vw] max-w-md" },
  3: { section: "items-end md:items-center justify-start md:justify-end", inner: "pb-[8vh] md:pb-0 px-6 md:pl-0 md:pr-[6vw] max-w-xl w-full" },
  4: { section: "items-end justify-start", inner: "pb-[7vh] pl-6 sm:pl-[6vw] pr-6 max-w-lg" },
  5: { section: "items-end justify-start", inner: "pb-[7vh] pl-6 sm:pl-[6vw] pr-6 max-w-lg" },
  6: { section: "items-center justify-center", inner: "px-6 max-w-4xl w-full" },
  7: { section: "items-end md:items-center justify-start md:justify-end", inner: "pb-[8vh] md:pb-0 px-6 md:pl-0 md:pr-[6vw] max-w-xl w-full" },
  8: { section: "items-center justify-center", inner: "px-6 max-w-2xl text-center mt-[28vh]" },
};

const CategoriesList = () => (
  <div className="mt-8">
    {CATEGORIES_UI.map((c) => (
      <Link
        key={c.slug}
        to={`/portfolio?category=${c.slug}`}
        data-testid={`ch3-cat-${c.slug}`}
        className="group flex items-baseline justify-between gap-4 border-b border-white/10 py-2.5 sm:py-3 hover:border-[#C7A45D]/60 transition-colors duration-300"
      >
        <span className="hcc-serif text-xl sm:text-2xl lg:text-3xl text-[#F7F1E7] group-hover:text-[#C7A45D] transition-colors duration-300">
          {c.name}
        </span>
        <span className="text-[9px] sm:text-[10px] tracking-[0.16em] uppercase text-[#F7F1E7]/45 text-right opacity-60 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-300">
          {c.origins}
        </span>
      </Link>
    ))}
  </div>
);

const ChannelsGrid = () => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10 mt-10">
    {CHANNELS.map((ch, i) => (
      <div key={ch} data-testid={`channel-${i + 1}`} className="bg-[#101010]/85 px-4 sm:px-5 py-5 sm:py-7">
        <span className="text-[10px] text-[#C7A45D] tracking-[0.25em]">{String(i + 1).padStart(2, "0")}</span>
        <p className="mt-2 text-[11px] sm:text-sm tracking-[0.08em] uppercase text-[#F7F1E7]/85">{ch}</p>
      </div>
    ))}
  </div>
);

const StatsRows = () => (
  <div className="mt-8 space-y-4">
    {CH7_STATS.map((s) => (
      <div key={s.label} className="flex flex-wrap items-baseline gap-x-5 gap-y-1 border-b border-white/10 pb-3.5">
        <span className="hcc-serif text-xl sm:text-2xl lg:text-3xl text-[#C7A45D] whitespace-nowrap">{s.value}</span>
        <span className="text-[10px] sm:text-xs uppercase tracking-[0.14em] text-[#F7F1E7]/60">{s.label}</span>
      </div>
    ))}
  </div>
);

const HomeChapter = ({ chapter }) => {
  const pos = POSITIONS[chapter.id];
  const isFirst = chapter.id === 1;
  const H = isFirst ? "h1" : "h2";
  return (
    <section
      data-chapter={chapter.id}
      data-testid={`chapter-${chapter.id}`}
      className={`hcc-chapter relative flex h-screen w-full ${pos.section}`}
    >
      <div className={`ch-inner relative ${pos.inner} ${isFirst ? "opacity-0" : ""}`}>
        {chapter.label && (
          <p className="mb-6 text-[10px] sm:text-[11px] tracking-[0.4em] text-[#C7A45D] font-medium">{chapter.label}</p>
        )}
        <H
          data-testid={`chapter-${chapter.id}-headline`}
          className={`hcc-serif hcc-text-shadow font-medium text-[#F7F1E7] ${
            isFirst
              ? "text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
              : "text-3xl sm:text-4xl lg:text-5xl leading-[1.1]"
          }`}
        >
          {chapter.headline}
        </H>
        {chapter.body && (
          <p className="mt-6 text-sm sm:text-base font-light leading-relaxed text-[#F7F1E7]/70 hcc-text-shadow max-w-xl">
            {chapter.body}
          </p>
        )}
        {chapter.phrase && (
          <p className="hcc-serif mt-6 text-lg sm:text-xl italic text-[#C7A45D]/90 hcc-text-shadow">{chapter.phrase}</p>
        )}
        {chapter.custom === "categories" && <CategoriesList />}
        {chapter.custom === "channels" && <ChannelsGrid />}
        {chapter.custom === "stats" && <StatsRows />}
        {chapter.ctas && (
          <div className={`mt-8 flex flex-wrap gap-3 ${chapter.id === 8 ? "justify-center" : ""}`}>
            {chapter.ctas.map((c, i) => (
              <Link
                key={c.to + c.label}
                to={c.to}
                data-testid={c.testId}
                className={`inline-block px-6 py-3 text-[10px] sm:text-[11px] uppercase tracking-[0.2em] font-semibold transition-all duration-300 ${
                  i === 0
                    ? "bg-[#F7F1E7] text-[#111111] hover:bg-[#C7A45D]"
                    : "border border-[#C7A45D]/60 text-[#C7A45D] hover:bg-[#C7A45D] hover:text-[#111111]"
                }`}
              >
                {c.label}
              </Link>
            ))}
          </div>
        )}
        <div className={`hcc-rule mt-8 w-16 ${chapter.id === 8 ? "mx-auto" : ""}`} aria-hidden="true" />
      </div>
    </section>
  );
};

const CinematicHome = ({ isMobile }) => {
  const progressRef = useRef(0);
  const mouseRef = useRef({ x: 0, y: 0 });
  const wrapRef = useRef(null);
  const pinRef = useRef(null);
  const counterRef = useRef(null);
  const bordeauxRef = useRef(null);
  const hintRef = useRef(null);
  const worldRef = useRef(null);
  const indiaRef = useRef(null);
  const [sceneReady, setSceneReady] = useState(false);

  useEffect(() => {
    const onMove = (e) => {
      mouseRef.current.x = (e.clientX / window.innerWidth) * 2 - 1;
      mouseRef.current.y = (e.clientY / window.innerHeight) * 2 - 1;
    };
    window.addEventListener("pointermove", onMove, { passive: true });
    return () => window.removeEventListener("pointermove", onMove);
  }, []);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    lenis.on("scroll", ScrollTrigger.update);
    const raf = (time) => lenis.raf(time * 1000);
    gsap.ticker.add(raf);
    gsap.ticker.lagSmoothing(0);

    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: pinRef.current,
        start: "top top",
        end: "bottom bottom",
        onUpdate: (self) => {
          const p = self.progress;
          progressRef.current = p;
          if (counterRef.current) {
            counterRef.current.textContent = `0${Math.min(8, Math.round(p * 7) + 1)}`;
            counterRef.current.parentElement.style.opacity = p > 0.985 ? "0" : "";
          }
          if (bordeauxRef.current) bordeauxRef.current.style.opacity = Math.min(0.9, Math.max(0, (p - 0.86) * 3)).toFixed(3);
          if (hintRef.current) hintRef.current.style.opacity = Math.max(0, 1 - p * 24).toFixed(3);
          if (worldRef.current) worldRef.current.style.opacity = windowOpacity(p, 0.365, 0.415, 0.455, 0.505).toFixed(3);
          if (indiaRef.current) indiaRef.current.style.opacity = windowOpacity(p, 0.505, 0.555, 0.60, 0.65).toFixed(3);
        },
      });

      const sections = gsap.utils.toArray(".hcc-chapter");
      sections.forEach((sec, i) => {
        const inner = sec.querySelector(".ch-inner");
        if (i > 0) {
          gsap.fromTo(
            inner,
            { opacity: 0, y: 90 },
            { opacity: 1, y: 0, ease: "none", scrollTrigger: { trigger: sec, start: "top 72%", end: "top 28%", scrub: true } }
          );
        }
        if (i < sections.length - 1) {
          gsap.fromTo(
            inner,
            { opacity: 1, y: 0 },
            {
              opacity: 0,
              y: -70,
              ease: "none",
              immediateRender: false,
              scrollTrigger: { trigger: sec, start: "center 38%", end: "bottom 18%", scrub: true },
            }
          );
        }
      });

      gsap.to('[data-chapter="1"] .ch-inner', {
        opacity: 1,
        y: 0,
        duration: 1.9,
        ease: "power3.out",
        delay: 1.9,
        startAt: { y: 50 },
      });
      gsap.fromTo(".hcc-counter, .hcc-hint", { opacity: 0 }, { opacity: 1, duration: 1.4, ease: "power2.out", delay: 2.3 });
    }, wrapRef);

    return () => {
      ctx.revert();
      gsap.ticker.remove(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <div ref={wrapRef} className="relative bg-[#111111]">
      <Preloader ready={sceneReady} />

      <div className="fixed inset-0 z-0" aria-hidden="true" data-testid="webgl-canvas-wrap">
        <BottleScene progressRef={progressRef} mouseRef={mouseRef} isMobile={isMobile} onReady={() => setSceneReady(true)} />
      </div>

      <div ref={bordeauxRef} className="hcc-bordeaux-glow fixed inset-0 z-[1] pointer-events-none opacity-0" aria-hidden="true" />
      <div className="hcc-vignette fixed inset-0 z-[2] pointer-events-none" aria-hidden="true" />
      <div className="hcc-grain fixed inset-0 z-[3] pointer-events-none opacity-[0.05]" aria-hidden="true" />

      {/* CH4 world routes overlay */}
      <div
        ref={worldRef}
        data-testid="world-routes-overlay"
        className="fixed z-[4] pointer-events-none opacity-0 left-[3vw] sm:left-[4vw] top-[8vh] w-[94vw] sm:w-[min(40vw,520px)]"
        aria-hidden="true"
      >
        <WorldRoutes />
      </div>

      {/* CH5 India map overlay */}
      <div
        ref={indiaRef}
        data-testid="india-map-overlay"
        className="fixed z-[4] pointer-events-none opacity-0 left-[12vw] sm:left-[28vw] top-[8vh] w-[58vw] sm:w-[min(26vw,330px)]"
        aria-hidden="true"
      >
        <IndiaDotMap />
      </div>

      <div data-testid="chapter-counter" className="hcc-counter fixed bottom-8 left-6 sm:left-10 z-20 flex items-baseline gap-2 opacity-0 pointer-events-none">
        <span ref={counterRef} className="hcc-serif text-xl text-[#C7A45D]">01</span>
        <span className="text-[10px] tracking-[0.3em] text-[#F7F1E7]/40">/ 08</span>
      </div>

      <div ref={hintRef} data-testid="scroll-hint" className="hcc-hint fixed bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-3 opacity-0 pointer-events-none">
        <span className="text-[9px] tracking-[0.5em] text-[#F7F1E7]/50 uppercase">Scroll</span>
        <div className="hcc-hint-line w-px h-10 bg-[#C7A45D]/70" />
      </div>

      {/* 800vh pinned narrative — 8 chapters */}
      <div ref={pinRef} data-testid="scroll-narrative" className="relative z-10">
        {HOME_CHAPTERS.map((c) => (
          <HomeChapter key={c.id} chapter={c} />
        ))}
      </div>

      <Ch9Section />
      <EndFrameBand />
    </div>
  );
};

export default function HomePage() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const forceStatic = params.get("mode") === "static";
  const forceReduced = params.get("mode") === "reduced";
  const prefersReduced = usePrefersReducedMotion();
  const webgl = useMemo(() => webglAvailable(), []);
  const isMobile = useIsMobile();

  if (forceStatic || !webgl) return <StaticHome heroType="image" />;
  if (forceReduced || prefersReduced) return <StaticHome heroType="canvas" isMobile={isMobile} />;
  return <CinematicHome isMobile={isMobile} />;
}
